// Política de operación SIN CONEXIÓN (F4 #53). Fuente única de verdad de qué se permite hacer
// offline. Es conservadora y fail-closed: solo un conjunto ADITIVO y mínimo de operaciones se
// habilita/encola sin red; todo lo demás exige conexión y la UI lo deshabilita.
//
// Principio (PLAN.md §Sincronización — política conservadora): el servidor es la ÚNICA autoridad.
// Esta política NO decide sobre seguridad: decide qué trabajo tiene sentido crear en el cliente sin
// red, para (a) no afirmar sin conexión nada crítico para la seguridad (el CIERRE, que declara el
// equipo liberado) que el servidor debería poder rechazar, y (b) no encolar acciones que el
// servidor tendría que rechazar en masa. Lo permitido es puramente aditivo y reversible por
// reconciliación.
//
//   Permitido offline (aditivo):   crear LOTO · colocar candado propio (sobre uno o varios
//                                   puntos de aislamiento, #244) · retirar un PUNTO de un
//                                   candado PROPIO (#245) · adjuntar foto de verificación.
//   Prohibido offline (requiere    retirar un punto de un candado AJENO (override) · CERRAR el
//   conexión, la UI lo deshabilita) LOTO (única transición del ciclo binario, #241) · aceptar
//                                   NDA · gestión de usuarios/roles · importación de maestros.
//
// (#245) La retirada es PUNTO A PUNTO y el candado se libera al sellar su último punto; el
// servidor lo resuelve en una transacción. La PROPIEDAD sigue siendo la del CANDADO
// (`colocado_por`), no la del punto: retirar cualquier punto de un candado ajeno es un override.
//
// (#54) Las fotos de verificación capturadas sin red se guardan como adjuntos en la outbox y el
// runner las sube al reconectar (subordinadas a la colocación del candado, no una entidad propia).
// El escaneo QR/código del candado es una AYUDA DE CAPTURA de solo lectura (rellena el código sin
// teclear con guantes): no genera mutación ni sincroniza, por eso no figura como operación aquí.

/** MENSAJE_REQUIERE_CONEXION es el texto corto para deshabilitar un control (title/tooltip). */
export const MENSAJE_REQUIERE_CONEXION = "Requiere conexión";

/**
 * MENSAJE_RETIRO_AJENO_OFFLINE amplía el motivo del caso más delicado: el retiro de un candado
 * ajeno es un override que solo el servidor puede autorizar (ADMIN/CUALIFICADO), y esa autoridad
 * no se puede comprobar sin red. Se deshabilita y jamás se encola.
 */
export const MENSAJE_RETIRO_AJENO_OFFLINE =
  "Requiere conexión: retirar un punto de un candado ajeno debe autorizarlo el servidor.";

/**
 * OPERACIONES_OFFLINE_PERMITIDAS / _PROHIBIDAS documentan la política como dato (además de en
 * prosa) para que un test la fije y no derive en silencio. No se consumen en caliente —los
 * predicados de abajo y los gates de la UI son quienes la aplican—, pero son el contrato legible.
 */
export const OPERACIONES_OFFLINE_PERMITIDAS = [
  "crear_loto",
  "colocar_candado_propio",
  "retirar_punto_propio",
  "adjuntar_foto_verificacion",
] as const;

export const OPERACIONES_OFFLINE_PROHIBIDAS = [
  "retirar_punto_ajeno",
  "cerrar_loto",
  "aceptar_nda",
  "gestion_usuarios",
  "importacion",
] as const;

/**
 * esRetiroPropio decide si un candado lo colocó el propio usuario. Es la frontera entre el retiro
 * PROPIO (permitido offline) y el AJENO (override, prohibido offline). Desde #245 la retirada es
 * por punto, pero la propiedad sigue siendo la del CANDADO: se compara `colocado_por` del candado,
 * no quién cubrió cada punto. Fail-closed: si no se conoce quién lo colocó o quién es el usuario,
 * se considera NO propio.
 */
export function esRetiroPropio(
  colocadoPor: number | null | undefined,
  meId: number | null | undefined,
): boolean {
  return colocadoPor != null && meId != null && colocadoPor === meId;
}

/**
 * puedeRetirarSinConexion: la retirada de un punto solo se permite sin red si el CANDADO es
 * PROPIO. Lo consumen tanto la UI (deshabilitar los botones «Retirar» de los puntos de un candado
 * ajeno offline) como la capa de mutación (rechazar su encolado como defensa en profundidad).
 */
export function puedeRetirarSinConexion(
  colocadoPor: number | null | undefined,
  meId: number | null | undefined,
): boolean {
  return esRetiroPropio(colocadoPor, meId);
}

/**
 * OfflinePolicyError señala que se intentó una operación sin conexión que la política offline no
 * permite. La capa de mutación la lanza como DEFENSA EN PROFUNDIDAD —aunque la UI deshabilite el
 * control— para garantizar que una acción prohibida NUNCA se encole; la UI la traduce a un aviso.
 */
export class OfflinePolicyError extends Error {
  constructor(message: string = MENSAJE_REQUIERE_CONEXION) {
    super(message);
    this.name = "OfflinePolicyError";
  }
}
